import { useEffect, useRef, useState } from 'preact/hooks';
import {
  activeLeadCard,
  appendMessage,
  closeLeadCardWithReopenPill,
  lastLeadCard,
} from '../store/widget-store';
import { getWsClient } from '../services/ws-client';
import { useI18n } from '../hooks/use-i18n';
import type { LeadField } from '@digital-cs/shared';

interface FieldMeta {
  label: string;
  placeholder: string;
  type: 'text' | 'tel' | 'email';
  autocomplete: string;
  maxLength: number;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric';
  /** 必填字段。微信号为可选辅助渠道，其余为强制。提交按钮需所有必填项填写完且至少一个字段非空。 */
  required: boolean;
}

// AI 客服留资仅收集姓名 + 手机号；其他渠道（wechat/email）保留在 LeadField 类型中以兼容
// 后台策略与历史数据，但本卡片不再渲染，BE 推送主站时也只会带这两项 + AI 摘要 + 来源 URL。
// 文案走 i18n(外文会话不再渲染中文表单)。
const fieldMeta = (
  t: (key: string) => string,
): Partial<Record<LeadField, FieldMeta>> => ({
  name:   { label: t('leadCard.nameLabel'),   placeholder: t('leadCard.namePlaceholder'),   type: 'text',  autocomplete: 'name',  maxLength: 64,  required: true },
  phone:  { label: t('leadCard.phoneLabel'),  placeholder: t('leadCard.phonePlaceholder'),  type: 'tel',   autocomplete: 'tel',   maxLength: 32,  required: true, inputMode: 'tel' },
});

const PHONE_RE = /^1[3-9]\d{9}$/;

/**
 * 留资卡 —— AI 评估满足条件时，BE 通过 SSE `lead.card.show` 推送 fields + prompt 触发渲染
 *
 * UX：AI 自然语言铺垫 + 嵌入式表单卡（混合方案）。
 * - 卡片仅显示后端 ask.fields 指定字段
 * - 提交 → POST /api/public/widget/lead → 成功后插入 system 消息致谢 + 关卡
 * - 底部 PIPL 合规说明
 */
export function LeadCaptureCard() {
  const { t } = useI18n();
  const FIELD_META = fieldMeta(t);
  const card = activeLeadCard.value;
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // 卡片出现时需要滚进视口。锚定渲染后卡片可能在消息流中部,不再一律拉到底:
  // scrollIntoView(block:'nearest') 只滚最小距离把卡片完整露出。双 rAF 等 layout 完成。
  // jsdom 无 scrollIntoView,兜底沿用旧逻辑(沿父链找滚动容器拉到底)。
  useEffect(() => {
    if (!card) return;
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const el = cardRef.current;
        if (!el) return;
        if (typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'nearest' });
          return;
        }
        let scroller: HTMLElement | null = el.parentElement;
        while (scroller) {
          const cs = getComputedStyle(scroller);
          if (cs.overflowY === 'auto' || cs.overflowY === 'scroll') break;
          scroller = scroller.parentElement;
        }
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [card]);

  if (!card) return null;

  const handleChange = (field: LeadField) => (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    setValues((prev) => ({ ...prev, [field]: target.value }));
  };

  // 后台策略历史上可能配置 wechat/email，但此卡片不再渲染这些字段。
  // 过滤掉 FIELD_META 未定义的字段，避免渲染时崩溃，也避免无 UI 的字段进入校验/提交。
  //
  // name + phone 是产品级硬约束(姓名 / 手机号强必填),与 BE 端 public-lead.controller.ts
  // 的 schema 一致;不论 cs.leadCriteria.requiredFields 推什么,UI 都强制把这两项加进来去重。
  // 顺序固定 name → phone → 其余,保证视觉一致。
  const HARD_REQUIRED: LeadField[] = ['name', 'phone'];
  const renderedFields: LeadField[] = Array.from(
    new Set<LeadField>([
      ...HARD_REQUIRED,
      ...card.fields.filter((f) => FIELD_META[f] !== undefined),
    ]),
  );

  const errors: Partial<Record<LeadField, string>> = {};
  for (const f of renderedFields) {
    const v = (values[f] ?? '').trim();
    if (!v) continue;
    if (f === 'phone' && !PHONE_RE.test(v)) errors.phone = t('leadCard.phoneError');
  }
  // 校验策略：所有必填项必须填写；至少一个字段有值；无格式错误。
  const allRequiredFilled = renderedFields
    .filter((f) => FIELD_META[f]?.required)
    .every((f) => (values[f] ?? '').trim().length > 0);
  const atLeastOneFilled = renderedFields.some(
    (f) => (values[f] ?? '').trim().length > 0,
  );
  const canSubmit =
    allRequiredFilled && atLeastOneFilled && Object.keys(errors).length === 0 && !submitting;

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    const payload: Record<string, string> = {};
    for (const f of renderedFields) {
      const v = (values[f] ?? '').trim();
      if (v) payload[f] = v;
    }
    // 附带 widget 嵌入页 URL（完整含 query/hash），BE 存到 lead.sourcePageUrl，
    // 后续推送主站 sourceUrl 字段，便于商务回访客户当时所在页面。
    payload.pageUrl = window.location.href;
    try {
      getWsClient().emit('visitor.lead.submit', payload);
      appendMessage({
        id: `m-lead-${Date.now()}`,
        role: 'system',
        text: t('leadCard.success'),
        ts: Date.now(),
      });
      activeLeadCard.value = null;
      // 清缓存 → 历史里所有 lead-reopen pill 渲染时返回 null,入口彻底消失
      lastLeadCard.value = null;
    } finally {
      setSubmitting(false);
    }
  };

  const dismiss = (): void => {
    // 收起卡片 + 消息流末尾留「再开」pill + 上报 BE,与新回合自动收起共用 store 动作
    closeLeadCardWithReopenPill();
  };

  return (
    <div ref={cardRef} class="dcs-lead-card" role="form" aria-label={t('leadCard.ariaForm')}>
      <div class="dcs-lead-card-header">
        <div class="dcs-lead-card-title">{t('leadCard.title')}</div>
        {card.prompt && <div class="dcs-lead-card-prompt">{card.prompt}</div>}
      </div>

      <div class="dcs-lead-card-fields">
        {renderedFields.map((field) => {
          const meta = FIELD_META[field]!;
          const err = errors[field];
          const fieldId = `dcs-lead-field-${field}`;
          return (
            <div key={field} class="dcs-lead-card-field">
              <label class="dcs-lead-card-label" htmlFor={fieldId}>
                {meta.label}
                {meta.required && (
                  <span class="dcs-lead-card-required" aria-hidden="true">*</span>
                )}
              </label>
              <input
                id={fieldId}
                class={`dcs-lead-card-input${err ? ' is-invalid' : ''}`}
                type={meta.type}
                autoComplete={meta.autocomplete}
                inputMode={meta.inputMode}
                maxLength={meta.maxLength}
                value={values[field] ?? ''}
                onInput={handleChange(field)}
                disabled={submitting}
                placeholder={meta.placeholder}
                aria-invalid={err ? 'true' : 'false'}
                aria-required={meta.required ? 'true' : 'false'}
              />
              {err && <div class="dcs-lead-card-error">{err}</div>}
            </div>
          );
        })}
      </div>

      <div class="dcs-lead-card-actions">
        <button
          type="button"
          class="dcs-lead-card-submit"
          onClick={submit}
          disabled={!canSubmit}
        >
          {submitting ? t('leadCard.submitting') : t('leadCard.submit')}
        </button>
        <button
          type="button"
          class="dcs-lead-card-dismiss"
          onClick={dismiss}
          disabled={submitting}
        >
          {t('leadCard.dismiss')}
        </button>
      </div>
    </div>
  );
}
